<template>
  <div class="lobby">
    <!-- Live Wins Ticker -->
    <div v-if="recentWins.length > 0" class="live-ticker">
      <div class="ticker-tag">
        <span class="ticker-dot"></span>
        {{ $t('lobby.live_wins') }}
      </div>
      <div class="ticker-scroll">
        <TransitionGroup name="ticker-item" tag="div" class="ticker-inner">
          <div v-for="win in recentWins" :key="win.id" class="ticker-item">
            <span class="ti-user">{{ win.username }}</span> {{ $t('lobby.won') }}
            <span class="ti-win">${{ formatAmount(win.win_amount) }}</span> {{ $t('lobby.on') }}
            <span class="ti-game">{{ win.game_name }}</span>
            <span v-if="win.multiplier >= 2" class="ti-multi">{{ win.multiplier.toFixed(2) }}x</span>
          </div>
        </TransitionGroup>
      </div>
    </div>

    <!-- Hero Section -->
    <div class="lobby-hero">
      <div class="lh-bg-grid"></div>
      <div class="lh-glow"></div>
      <div class="lh-content">
        <div class="lh-eyebrow">{{ $t('lobby.welcome_back') }}</div>
        <h1 class="lh-title">{{ $t('lobby.hero_title_prefix') }} <span>{{ $t('lobby.hero_title_highlight') }}</span> {{ $t('lobby.hero_title_suffix') }}</h1>
        <p class="lh-desc">{{ $t('lobby.hero_description') }}</p>
        <div class="lh-btns">
          <button class="btn-cta" @click="scrollToGames">{{ $t('lobby.play_now') }}</button>
        </div>
      </div>
    </div>

    <!-- Jurisdiction: game restriction notice -->
    <JurisdictionBlockNotice
      v-if="hasGameRestrictions"
      type="game"
      :message="$t('jurisdiction.game_default')"
      class="lobby-jurisdiction-notice"
    />

    <!-- Controls: Category Tabs + Search + Sort -->
    <div ref="gamesSection" class="lobby-controls">
      <div class="category-row">
        <button
          v-for="cat in categories"
          :key="cat.value ?? 'all'"
          class="cat-chip"
          :class="{ active: !showFavorites && store.activeCategory === cat.value }"
          @click="selectCategory(cat.value)"
        >
          {{ cat.label }}
        </button>
        <button
          class="cat-chip cat-chip--fav"
          :class="{ active: showFavorites }"
          @click="toggleFavoritesTab"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" :fill="showFavorites ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          {{ $t('lobby.favorites') }}
        </button>
      </div>

      <div class="controls-right">
        <div class="search-box">
          <span class="search-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <input
            v-model="searchInput"
            type="text"
            class="search-input"
            :placeholder="$t('lobby.search_placeholder')"
            @input="onSearchInput"
          >
        </div>

        <select v-model="sortValue" class="sort-select" @change="onSortChange">
          <option value="popular">{{ $t('lobby.sort_popular') }}</option>
          <option value="newest">{{ $t('lobby.sort_newest') }}</option>
          <option value="a-z">{{ $t('lobby.sort_az') }}</option>
          <option value="rtp">{{ $t('lobby.sort_rtp') }}</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="store.loading" class="games-grid">
      <div v-for="i in 10" :key="i" class="game-card-skeleton">
        <div class="gcs-thumb"></div>
        <div class="gcs-info">
          <div class="gcs-name"></div>
          <div class="gcs-rtp"></div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="store.error" class="lobby-empty">
      <p>{{ store.error }}</p>
      <button class="btn-cta" @click="store.fetchGames(1)">{{ $t('lobby.try_again') }}</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="displayedGames.length === 0" class="lobby-empty">
      <p>{{ showFavorites ? $t('lobby.no_favorites') : $t('lobby.no_games') }}</p>
      <p class="lobby-empty-sub">{{ showFavorites ? $t('lobby.no_favorites_sub') : $t('lobby.no_games_sub') }}</p>
    </div>

    <!-- Games Grid -->
    <div v-else>
      <div class="games-grid">
        <div
          v-for="game in displayedGames"
          :key="game.id"
          class="game-card"
          @click="navigateToGame(game.slug)"
        >
          <div
            class="gc-thumb"
            :class="gameAssets[game.slug] ? '' : getGradientClass(game.slug)"
            :style="gameAssets[game.slug]?.bg ? { backgroundImage: `url(${gameAssets[game.slug]!.bg})` } : undefined"
          >
            <img
              v-if="gameAssets[game.slug]?.logo"
              :src="gameAssets[game.slug]!.logo!"
              :alt="game.title"
              class="gc-logo"
            />
            <span v-else class="gc-emoji"><AppIcon :name="getCategoryIcon(game.category)" :size="16" /></span>
            <button
              class="gc-fav-btn"
              :class="{ active: store.isFavorited(game.id) }"
              :aria-label="$t('lobby.toggle_favorite')"
              @click.stop="store.toggleFavorite(game.id)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" :fill="store.isFavorited(game.id) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>
            <span
              v-for="badge in game.badges"
              :key="badge"
              class="gc-badge"
              :class="'gc-badge--' + badge.toLowerCase()"
            >
              {{ badge }}
            </span>
          </div>
          <div class="gc-info">
            <div class="gc-name" :title="game.title">{{ game.title }}</div>
            <div v-if="game.rtp !== null" class="gc-rtp">
              {{ $t('lobby.rtp_label') }} <span>{{ game.rtp }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Load More -->
      <div v-if="!showFavorites && store.hasMore" class="load-more">
        <button
          class="btn-load-more"
          :disabled="store.loadingMore"
          @click="store.loadMore()"
        >
          {{ store.loadingMore ? $t('common.loading') : $t('lobby.load_more') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGamesStore } from '@/stores/games'
import AppIcon from '@/components/AppIcon.vue'
import JurisdictionBlockNotice from '@/components/JurisdictionBlockNotice.vue'
import type { SortOption } from '@/stores/games'
import { subscribeToPublicChannel, unsubscribe } from '@/composables/useEcho'
import { useJurisdiction } from '@/composables/useJurisdiction'

interface RecentWinEvent {
  game_name: string
  username: string
  win_amount: number
  multiplier: number
}

interface RecentWinItem extends RecentWinEvent {
  id: number
}

const LOBBY_CHANNEL = 'lobby'
const MAX_WINS = 10
let winIdCounter = 0

const props = withDefaults(defineProps<{
  defaultCategory?: string | null
}>(), {
  defaultCategory: null,
})

const router = useRouter()
const { t } = useI18n()
const store = useGamesStore()
const { jurisdiction, fetchJurisdiction } = useJurisdiction()

const hasGameRestrictions = computed(() =>
  (jurisdiction.value?.blocked_games?.length ?? 0) > 0,
)

const searchInput = ref(store.searchQuery)
const sortValue = ref<SortOption>('popular')

// Keep lobby input in sync when header search changes the store
watch(() => store.searchQuery, (val) => {
  if (val !== searchInput.value) searchInput.value = val
})
const gamesSection = ref<HTMLElement | null>(null)
const showFavorites = ref(false)
const recentWins = ref<RecentWinItem[]>([])

const categories = computed<Array<{ label: string; value: string | null }>>(() => [
  { label: t('lobby.all_games'), value: null },
  { label: t('lobby.slots'), value: 'slots' },
  { label: t('lobby.table'), value: 'table' },
  { label: t('lobby.instant'), value: 'instant' },
])

const gameAssets = reactive<Record<string, { bg: string | null; logo: string | null }>>({})

function probeImage(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(src)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

async function probeGameAssets(slug: string, category: string): Promise<void> {
  if (slug in gameAssets) return
  const dir = slug.replace(/-/g, '_')
  const assetDir = category === 'slots'
    ? `slots/${dir}`
    : category === 'table'
      ? `table/${dir}`
      : `instant/${dir}`
  const [bg, logo] = await Promise.all([
    probeImage(`/assets/${assetDir}/background.jpg`),
    probeImage(`/assets/${assetDir}/logo.png`),
  ])
  if (bg || logo) {
    gameAssets[slug] = { bg, logo }
  }
}

const displayedGames = computed(() => {
  let list = store.games
  const q = store.searchQuery.trim().toLowerCase()
  if (q) {
    list = list.filter(game => game.title.toLowerCase().includes(q) || game.slug.toLowerCase().includes(q))
  }
  if (showFavorites.value) {
    list = list.filter(game => store.isFavorited(game.id))
  }
  return list
})

const gradientClasses = [
  'gt1', 'gt2', 'gt3', 'gt4', 'gt5',
  'gt6', 'gt7', 'gt8', 'gt9', 'gt10',
]

function getGradientClass(slug: string): string {
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash)
  }
  return gradientClasses[Math.abs(hash) % gradientClasses.length]
}

function getCategoryIcon(slug: string): string {
  const map: Record<string, string> = {
    slots: 'category-slots',
    table: 'category-table',
    instant: 'category-instant',
    'video-poker': 'category-video-poker',
  }
  return map[slug] ?? 'category-default'
}

function onSearchInput() {
  store.searchQuery = searchInput.value
}

function onSortChange() {
  store.setSort(sortValue.value)
}

function navigateToGame(slug: string) {
  router.push({ name: 'player.game', params: { slug } })
}

function selectCategory(value: string | null) {
  showFavorites.value = false
  store.setCategory(value)
}

function toggleFavoritesTab() {
  showFavorites.value = !showFavorites.value
  if (showFavorites.value) {
    // Load all games so we can filter client-side
    store.setCategory(null)
  }
}

function scrollToGames() {
  gamesSection.value?.scrollIntoView({ behavior: 'smooth' })
}

function formatAmount(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function handleRecentWin(data: RecentWinEvent): void {
  const win: RecentWinItem = { ...data, id: ++winIdCounter }
  recentWins.value.unshift(win)
  if (recentWins.value.length > MAX_WINS) {
    recentWins.value.pop()
  }
}

// Sync category selection whenever the route's defaultCategory prop changes,
// including the initial mount (immediate: true). This handles navigation
// between /player/games/slots, /player/games/table, etc. without remounting.
watch(
  () => props.defaultCategory,
  (cat) => {
    selectCategory(cat ?? null)
  },
  { immediate: true },
)

watch(
  () => store.games,
  (games) => {
    for (const game of games) {
      probeGameAssets(game.slug, game.category)
    }
  },
  { immediate: true },
)

onMounted(() => {
  store.fetchFavorites()
  subscribeToPublicChannel<RecentWinEvent>(LOBBY_CHANNEL, '.recent.win', handleRecentWin)
  // Fetch jurisdiction so the game restriction notice can be shown if applicable
  fetchJurisdiction()
})

onUnmounted(() => {
  unsubscribe(LOBBY_CHANNEL)
})
</script>

<style scoped>
.lobby {
  padding: var(--space-6, 24px);
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Hero */
.lobby-hero {
  position: relative;
  background: linear-gradient(135deg, #0d2238 0%, var(--bg-primary, #0F1923) 60%);
  border-radius: var(--radius-xl, 16px);
  padding: 40px 36px;
  margin-bottom: 28px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.lh-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.lh-glow {
  position: absolute;
  right: -60px;
  top: -60px;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 231, 1, 0.1) 0%, transparent 70%);
  pointer-events: none;
}

.lh-content {
  position: relative;
  z-index: 1;
}

.lh-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 231, 1, 0.1);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: var(--radius-full, 9999px);
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-primary, #00e701);
  margin-bottom: 12px;
}

.lh-title {
  font-family: var(--font-family-display, 'Russo One', sans-serif);
  font-size: clamp(22px, 3vw, 36px);
  line-height: 1.15;
  margin-bottom: 10px;
}

.lh-title span {
  color: var(--accent-primary, #00e701);
}

.lh-desc {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  max-width: 400px;
  line-height: 1.65;
  margin-bottom: 20px;
}

.lh-btns {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-cta {
  padding: 11px 22px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-cta:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

/* Controls Row */
.lobby-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.category-row {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  flex-shrink: 0;
}

.category-row::-webkit-scrollbar {
  height: 0;
}

.cat-chip {
  padding: 7px 16px;
  border-radius: var(--radius-full, 9999px);
  font-size: 12px;
  font-weight: 600;
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--transition-fast, 150ms ease);
  flex-shrink: 0;
  font-family: inherit;
}

.cat-chip.active,
.cat-chip:hover {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.25);
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  pointer-events: none;
  display: flex;
  align-items: center;
}

.search-input {
  width: 200px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 8px 12px 8px 34px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  transition: border-color var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.search-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.sort-select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 8px 12px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  cursor: pointer;
  transition: border-color var(--transition-fast, 150ms ease);
  font-family: inherit;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237f8fa4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 30px;
}

.sort-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.sort-select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

/* Games Grid */
.games-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.game-card {
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all var(--transition-normal, 250ms ease);
  position: relative;
}

.game-card:hover {
  transform: scale(1.04);
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.4));
  z-index: 2;
}

.game-card:hover .gc-thumb::after {
  opacity: 1;
}

.gc-thumb {
  aspect-ratio: 3 / 4;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-size: cover;
  background-position: center;
}

.gc-logo {
  max-width: 80%;
  max-height: 45%;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.6));
  z-index: 1;
}

.gc-thumb::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 18px 4px rgba(0, 231, 1, 0.35);
  opacity: 0;
  transition: opacity var(--transition-fast, 150ms ease);
  pointer-events: none;
  z-index: 2;
}

.gc-emoji {
  font-size: 44px;
  line-height: 1;
}

.gc-fav-btn {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary, #b1bad3);
  transition: all var(--transition-fast, 150ms ease);
  z-index: 3;
  opacity: 0;
  backdrop-filter: blur(4px);
}

.game-card:hover .gc-fav-btn {
  opacity: 1;
}

.gc-fav-btn.active {
  opacity: 1;
  color: var(--color-danger, #ed4245);
  background: rgba(237, 66, 69, 0.2);
}

.gc-fav-btn:hover {
  transform: scale(1.15);
  color: var(--color-danger, #ed4245);
}

.cat-chip--fav {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.cat-chip--fav.active {
  background: rgba(237, 66, 69, 0.1);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.25);
}

.gc-badge {
  position: absolute;
  top: 7px;
  left: 7px;
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 3px 7px;
  border-radius: var(--radius-sm, 4px);
  z-index: 1;
  background: rgba(0, 231, 1, 0.2);
  color: var(--accent-primary, #00e701);
}

.gc-badge--hot {
  background: rgba(237, 66, 69, 0.2);
  color: var(--color-danger, #ed4245);
}

.gc-badge--new {
  background: rgba(20, 117, 225, 0.2);
  color: var(--accent-secondary, #1475e1);
}

.gc-badge--popular {
  background: rgba(255, 215, 0, 0.2);
  color: var(--accent-gold, #ffd700);
}

.gc-info {
  padding: 10px 10px 9px;
  background: var(--bg-tertiary, #213743);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.gc-name {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gc-rtp {
  font-size: 10px;
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  color: var(--text-tertiary, #7f8fa4);
}

.gc-rtp span {
  color: var(--color-success, #00e701);
}

/* Gradient backgrounds */
.gt1 { background: var(--gradient-gt1, linear-gradient(135deg, #0d1a2b, #0d3a6b)); }
.gt2 { background: var(--gradient-gt2, linear-gradient(135deg, #1a0d1a, #5a0080)); }
.gt3 { background: var(--gradient-gt3, linear-gradient(135deg, #2b0d0d, #8b2020)); }
.gt4 { background: var(--gradient-gt4, linear-gradient(135deg, #0d2b0d, #1a6b1a)); }
.gt5 { background: var(--gradient-gt5, linear-gradient(135deg, #2b2b0d, #8b7a00)); }
.gt6 { background: var(--gradient-gt6, linear-gradient(135deg, #2b1a0d, #8b5a00)); }
.gt7 { background: var(--gradient-gt7, linear-gradient(135deg, #0d2b2b, #00728b)); }
.gt8 { background: var(--gradient-gt8, linear-gradient(135deg, #1f0d2b, #6a0080)); }
.gt9 { background: var(--gradient-gt9, linear-gradient(135deg, #1a1a1a, #4a4a4a)); }
.gt10 { background: var(--gradient-gt10, linear-gradient(135deg, #0d1a1a, #006b6b)); }

/* Skeleton loading */
.game-card-skeleton {
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.gcs-thumb {
  aspect-ratio: 3 / 4;
  background: linear-gradient(
    90deg,
    var(--bg-secondary, #1a2c38) 25%,
    var(--bg-tertiary, #213743) 50%,
    var(--bg-secondary, #1a2c38) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.gcs-info {
  padding: 10px;
  background: var(--bg-tertiary, #213743);
}

.gcs-name {
  height: 12px;
  width: 70%;
  border-radius: 4px;
  margin-bottom: 6px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.gcs-rtp {
  height: 10px;
  width: 40%;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Jurisdiction notice in lobby */
.lobby-jurisdiction-notice {
  margin-bottom: 16px;
}

/* Empty/error state */
.lobby-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 16px;
  gap: 12px;
}

.lobby-empty-sub {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Load More */
.load-more {
  display: flex;
  justify-content: center;
  padding: 12px 0 24px;
}

.btn-load-more {
  padding: 10px 32px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-weight: 600;
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-load-more:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-load-more:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1200px) {
  .games-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .lobby {
    padding: var(--space-4, 16px);
  }

  .lobby-hero {
    padding: 20px;
  }

  .games-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .lobby-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .controls-right {
    width: 100%;
  }

  .search-input {
    flex: 1;
    width: 100%;
  }

  .search-box {
    flex: 1;
  }
}

/* Live Wins Ticker */
.live-ticker {
  height: 32px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-bottom: 16px;
}

.ticker-tag {
  padding: 0 14px;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-tertiary, #213743);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-primary, #00e701);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.ticker-dot {
  width: 6px;
  height: 6px;
  background: var(--accent-primary, #00e701);
  border-radius: 50%;
  animation: pulse 1.4s ease infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.ticker-scroll {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.ticker-inner {
  display: flex;
  white-space: nowrap;
}

.ticker-item {
  padding: 0 24px;
  font-size: 11px;
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  color: var(--text-tertiary, #7f8fa4);
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.ti-user {
  color: var(--text-primary, #fff);
}

.ti-win {
  color: var(--color-success, #00e701);
  font-weight: 700;
}

.ti-game {
  color: var(--accent-secondary, #1475e1);
}

.ti-multi {
  color: var(--accent-gold, #ffd700);
  font-weight: 600;
  margin-left: 4px;
}

/* Ticker item transitions */
.ticker-item-enter-active {
  transition: all 0.3s ease;
}

.ticker-item-leave-active {
  transition: all 0.2s ease;
  position: absolute;
}

.ticker-item-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}

.ticker-item-leave-to {
  opacity: 0;
}

/* RTL Support */
/* Hero glow: mirror from right to left */
[dir='rtl'] .lh-glow {
  right: unset;
  left: -60px;
}

/* Search: flip icon and padding */
[dir='rtl'] .search-icon {
  left: unset;
  right: 10px;
}

[dir='rtl'] .search-input {
  padding: 8px 12px 8px 34px;
}

/* Sort select: move chevron arrow to left side */
[dir='rtl'] .sort-select {
  background-position: left 10px center;
  padding-right: 12px;
  padding-left: 30px;
}

/* Game card favorite button: mirror to left corner */
[dir='rtl'] .gc-fav-btn {
  right: unset;
  left: 7px;
}

/* Game card badges: mirror to right corner */
[dir='rtl'] .gc-badge {
  left: unset;
  right: 7px;
}

/* Live wins ticker: flip separator border */
[dir='rtl'] .ticker-tag {
  border-right: none;
  border-left: 1px solid rgba(255, 255, 255, 0.06);
}

/* Monetary amounts in ticker: always LTR */
[dir='rtl'] .ti-win,
[dir='rtl'] .ti-multi {
  direction: ltr;
  unicode-bidi: embed;
  display: inline-block;
}
</style>
